import { useEffect, useMemo, useState } from "react";
import { ENDPOINT } from "apiRoutes";

function buildBaseUrl(baseUrl) {
    return (baseUrl || ENDPOINT || "").replace(/\/$/, "");
}

function extractStatus(payload) {
    return (
        payload?.status ||
        payload?.view?.status ||
        payload?.data?.status ||
        payload?.view?.data?.status ||
        null
    );
}

function extractOrderNumber(payload) {
    return (
        payload?.orderNumber ||
        payload?.view?.orderNumber ||
        payload?.data?.orderNumber ||
        payload?.view?.data?.orderNumber ||
        null
    );
}

export function useOrderStatus({ baseUrl, companyId, orderId }) {
    const [status, setStatus] = useState(null);
    const [orderNumber, setOrderNumber] = useState(null);
    const [connected, setConnected] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const resolvedBaseUrl = useMemo(() => buildBaseUrl(baseUrl), [baseUrl]);

    useEffect(() => {
        if (!companyId || !orderId || !resolvedBaseUrl) return;

        let cancelled = false;
        let stream;

        const loadInitialStatusAndSubscribe = async () => {
            try {
                setLoading(true);
                setError(null);

                const initialUrl = `${resolvedBaseUrl}/api/restaurant/orders/${orderId}/public-status?companyId=${encodeURIComponent(companyId)}`;
                const response = await fetch(initialUrl);

                if (!response.ok) {
                    throw new Error(`Initial status request failed (${response.status})`);
                }

                const data = await response.json();
                if (!cancelled) {
                    setStatus(extractStatus(data));
                    setOrderNumber(extractOrderNumber(data));
                }

                const streamUrl = `${resolvedBaseUrl}/api/restaurant/orders/${orderId}/stream?companyId=${encodeURIComponent(companyId)}`;
                stream = new EventSource(streamUrl);

                stream.addEventListener("order_status", (event) => {
                    try {
                        const payload = JSON.parse(event.data || "{}");
                        if (!cancelled) {
                            setStatus(extractStatus(payload));
                            const streamedOrderNumber = extractOrderNumber(payload);
                            if (streamedOrderNumber) {
                                setOrderNumber(streamedOrderNumber);
                            }
                        }
                    } catch (_err) {
                        // Ignore malformed SSE payloads; subsequent valid events still update status.
                    }
                });

                stream.onopen = () => {
                    if (!cancelled) {
                        setConnected(true);
                    }
                };

                stream.onerror = () => {
                    if (!cancelled) {
                        setConnected(false);
                    }
                };
            } catch (err) {
                if (!cancelled) {
                    setError(err.message || "Unable to load order status");
                }
            } finally {
                if (!cancelled) {
                    setLoading(false);
                }
            }
        };

        loadInitialStatusAndSubscribe();

        return () => {
            cancelled = true;
            setConnected(false);
            if (stream) {
                stream.close();
            }
        };
    }, [companyId, orderId, resolvedBaseUrl]);

    return { status, orderNumber, connected, loading, error };
}
